import React, { useEffect, useMemo, useState } from 'react';
import { Table, Row, Col, Card, Typography, Select, Button, Image, List, Tag, Breadcrumb, Space, Empty, Segmented, Spin, notification } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { onlyYear, onlyYears, PictureCell, unexpectedError, intialPaginationState, showingPageTotalRange } from '../../../common_functions';
import { SORT_OPTIONS, getDirectusSort, formatPlate } from './bibliographyUtils';
import { NoImageIcon as FALLBACK_IMG } from '../../icons';
import { ImageWithLoader } from '../../resuable-comp';
const { Title, Text } = Typography;

const ImagePublicationVariants = props => {
  // pagination + data
  const [pagination, setPagination] = useState(intialPaginationState);
  const [reports, setReports] = useState([]);

  // selection
  const [books, setBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [viewMode, setViewMode] = useState('GRID'); // GRID | LIST

  // sort
  const [sortBy, setSortBy] = useState('PLATE_ASC'); // PLATE_ASC | PLATE_DESC | TITLE_ASC | TITLE_DESC

  const bookDetails = useMemo(() => books.find(b => b.id === selectedBookId), [books, selectedBookId]);


  const buildIllustrationFilters = (bookID = selectedBookId) => {
    const andFilters = [{ books: { id: { _eq: bookID } } }];

    return { _and: andFilters };
  };

  // ---------- data fetching ----------
  const getBooks = async () => {
    try {
      setLoading(true);
      const { data } = await props.getItems('books', {
        fields: ['*', 'artist.First_Name', 'artist.last_name'],
        sort: 'book_title',
        limit: '-1',
      });

      setBooks(data || []);
      const firstId = data?.[0]?.id ?? null;
      setSelectedBookId(firstId);

      if (firstId) {
        const next = { ...intialPaginationState };
        setPagination(next);
        await getObjectsData(next, buildIllustrationFilters(firstId), firstId, sortBy);
      }
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBooks();
  }, []);

  const getObjectsData = async (paginationArg, filtersArg, bookID = selectedBookId, sortKey = sortBy) => {
    try {
      setLoading(true);

      const response = await props.getBookIllustrations({
        fields: 'id,image,books,books.book_title,plate,image_title,negative.obj_name,books.editor,books.publisher,books.date_of_publication,negative.creation_earliest,negative.creation_latest',
        sort: getDirectusSort(sortKey),
        filters: filtersArg,
        offset: paginationArg.pageSize * (paginationArg.current - 1),
        limit: paginationArg.pageSize,
        meta: 'filter_count',
      });

      setReports(response.data || []);
      setPagination(prev => ({ ...prev, ...paginationArg, total: response.meta?.filter_count ?? 0 }));
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const onBookChange = async bookID => {
    setSelectedBookId(bookID);
    const next = { ...pagination, current: 1 };
    setPagination(next);
    await getObjectsData(next, buildIllustrationFilters(bookID), bookID, sortBy);
  };

  const generatePDF = async (id = null) => {
    try {
      setPdfLoading(true);

      await props.BibliographyPdfReport({
        book_id: selectedBookId,
        filters: buildIllustrationFilters(selectedBookId),
        sort_by: getDirectusSort(sortBy),
      });

      notification.success({
        message: 'Report Queued',
        description: 'Your report is being generated. You will be notified by email when it is ready. Download it from Report History.',
        placement: 'topRight',
        duration: 6,
      });
    } catch (error) {
      console.error('PDF generation failed:', error);
      notification.error({
        message: 'Report Generation Failed',
        description: error?.response?.data?.error || error?.message || 'Something went wrong while generating the report.',
        placement: 'topRight',
      });
    } finally {
      setPdfLoading(false);
    }
  };
  if (!bookDetails) return null;

  const tableColumns = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 120,
      render: (image, record) => (
        <ImageWithLoader loading={loading} height={70}>
          <PictureCell dataIndexValue={image} getFile={props.getFile} width={100} style={{ width: 100, height: 70, objectFit: 'cover' }} />
        </ImageWithLoader>
      ),
    },
    {
      title: 'Image Title',
      dataIndex: 'image_title',
      key: 'image_title',
      render: value => value || '-',
    },
    {
      title: 'Negative #',
      key: 'negative_object',
      render: (_, record) => record?.negative?.obj_name || '-',
    },
    {
      title: 'Negative Date',
      key: 'negative',
      render: (_, record) => onlyYears(record?.negative?.creation_earliest, record?.negative?.creation_latest) || '-',
    },

    {
      title: 'Plate #',
      dataIndex: 'plate',
      key: 'plate',
      width: 100,
      render: value => formatPlate(value),
    },
  ];

  return (
    <>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Spin size="large" />
        </div>
      )}
      <Row>
        {/* Breadcrumb */}
        <Col span={24} style={{ marginBottom: 16 }}>
          <Breadcrumb items={[{ title: "Reports", href: "/reports/bibliography-report/pub-list-with-variants" }, { title: "Plate Inventory per Book" }]} />
        </Col>

        <Col span={24} className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Plate Inventory per Book
              </Typography.Title>
            </Col>
          </Row>

          {/* Filters (top bar like screenshot) */}
          <Card style={{ marginBottom: 20 }}>
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} md={8} lg={6}>
                <Text type="secondary">Book Title</Text>
                <Select
                  style={{ width: '100%', marginTop: 6 }}
                  value={selectedBookId}
                  onChange={onBookChange}
                  options={books.map(b => ({ value: b.id, label: b.book_title }))}
                  placeholder="Select book"
                />
              </Col>
            </Row>
          </Card>

          {/* Book Details */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8} md={6} lg={5}>
                {bookDetails?.cover_photo ? (
                  <PictureCell
                    dataIndexValue={bookDetails.cover_photo}
                    getFile={props.getFile}
                    width={100}
                    style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 10 }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: 180,
                      borderRadius: 10,
                      overflow: 'hidden',
                    }}
                  >
                    <FALLBACK_IMG
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </div>
                )}
              </Col>

              <Col xs={24} sm={24} md={18} lg={19}>
                <Row justify="space-between" align="top" gutter={[12, 12]}>
                  <Col flex="auto">
                    <Title level={4} style={{ marginBottom: 6 }}>
                      {bookDetails.book_title}
                    </Title>
                    <Text type="secondary">
                      {bookDetails?.artist?.First_Name} {bookDetails?.artist?.last_name}
                    </Text>
                  </Col>
                  <Col>
                    <Button type="primary" icon={<FilePdfOutlined />} loading={pdfLoading} onClick={generatePDF} aria-label="Download PDF">
                      Download Pdf
                    </Button>
                  </Col>
                </Row>

                <Row gutter={[16, 10]} style={{ marginTop: 12 }}>
                  <Col xs={24} md={12} lg={8}>
                    <Text strong>Editor:</Text> <Text>{bookDetails.editor || '-'}</Text>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Text strong>Author:</Text> <Text>{bookDetails.author || '-'}</Text>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Text strong>ISBN:</Text> <Text>{bookDetails.isbn || '-'}</Text>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Text strong>Edition:</Text> <Text>{bookDetails.edition || '-'}</Text>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Text strong>Publisher:</Text> <Text>{bookDetails.publisher || '-'}</Text>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Text strong>Publication Date:</Text> <Text>{onlyYear(bookDetails.date_of_publication) || '-'}</Text>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
          {/* Illustrations (toolbar like screenshot) */}
          <Card
            style={{ marginTop: 16 }}
            title={
              // <Row align="middle" justify="space-between" style={{ width: '100%' }}>
              <Row align="middle" justify="space-between" gutter={24} style={{ width: '100%' }}>
                {/* LEFT */}
                <Col xs={24} sm={24} md={12}>
                  <Space size="middle">
                    <span>Illustrations</span>
                    <Tag className="ant-tag-blue">({pagination.total})</Tag>
                  </Space>
                </Col>

                {/* RIGHT */}
                <Col xs={24} sm={24} md={12} style={{ textAlign: 'right' }}>
                  <Space size="middle" wrap>
                    {/* View Switch */}
                    <Space align="center" size={6}>
                      <Text type="secondary">View</Text>
                      <Segmented
                        className="ant-tag-blue"
                        size="small"
                        value={viewMode}
                        onChange={setViewMode}
                        options={[
                          { value: 'GRID', label: 'Grid' },
                          { value: 'LIST', label: 'List' },
                        ]}
                      />
                    </Space>

                    <Text type="secondary">|</Text>

                    {/* Sort */}
                    <Space align="center" size={6}>
                      <Text type="secondary">Sort by</Text>
                      <Select
                        size="small"
                        value={sortBy}
                        style={{ minWidth: 140 }}
                        onChange={async value => {
                          setSortBy(value);
                          const next = { ...pagination, current: 1 };
                          setPagination(next);
                          await getObjectsData(next, buildIllustrationFilters(), selectedBookId, value);
                        }}
                        options={SORT_OPTIONS}
                      />
                    </Space>
                  </Space>
                </Col>
              </Row>
            }
          >
            {reports.length === 0 ? (
              <Empty description="No illustrations found." />
            ) : viewMode === 'GRID' ? (
              <Image.PreviewGroup>
                <List
                  loading={loading}
                  grid={
                    viewMode === 'GRID'
                      ? {
                          gutter: 16,
                          xs: 1,
                          sm: 2,
                          md: 3,
                          lg: 4,
                        }
                      : undefined
                  }
                  itemLayout={viewMode === 'LIST' ? 'horizontal' : undefined}
                  dataSource={reports}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    responsive: true,
                    showSizeChanger: true,
                    ...showingPageTotalRange,
                    onChange: async (page, pageSize) => {
                      const next = { ...pagination, current: page, pageSize };
                      setPagination(next);
                      await getObjectsData(next, buildIllustrationFilters(), selectedBookId, sortBy);
                    },
                  }}
                  renderItem={item => (
                    <List.Item>
                      {viewMode === 'GRID' && (
                        <Card hoverable styles={{ body: { padding: 12 } }}>
                          <div
                            style={{
                              width: '100%',
                              height: 160,
                              borderRadius: 10,
                              display: 'flex',
                              overflow: 'hidden',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PictureCell
                              dataIndexValue={item.image}
                              getFile={props.getFile}
                              width={100}
                              style={{ width: '100%', height: 160, objectFit: 'cover' }}
                            />
                          </div>

                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12 }}>
                              <Text strong>Image Title:</Text> <Text>{item.image_title || '-'}</Text>
                            </div>
                            <div style={{ marginTop: 6, fontSize: 12 }}>
                              <Text strong>Negative Date:</Text> <Text>{item?.negative?.obj_name}</Text>
                            </div>
                            <div style={{ marginTop: 6, fontSize: 12 }}>
                              <Text strong>Negative:</Text>{' '}
                              <Text>{onlyYears(item?.negative?.creation_earliest, item?.negative?.creation_latest) || '-'}</Text>
                            </div>
                            <div style={{ marginTop: 6, fontSize: 12 }}>
                              <Text strong>Plate#:</Text> <Text>{formatPlate(item.plate)}</Text>
                            </div>
                          </div>
                        </Card>
                      )}
                    </List.Item>
                  )}
                />
              </Image.PreviewGroup>
            ) : (
              <Table
                className="no-fixed-last-col"
                rowKey="id"
                loading={loading}
                columns={tableColumns}
                dataSource={reports}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  ...showingPageTotalRange,
                  onChange: async (page, pageSize) => {
                    const next = { ...pagination, current: page, pageSize };
                    setPagination(next);
                    await getObjectsData(next, buildIllustrationFilters(), selectedBookId, sortBy);
                  },
                }}
                scroll={{ x: 800 }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ImagePublicationVariants;
